import React, { useState } from 'react';
import {
  Heart,
  MessageCircle,
  Share2,
  Eye,
  ExternalLink,
  ImageOff,
  Instagram,
  Music,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
  DollarSign,
  MousePointerClick,
  Navigation
} from 'lucide-react';
import { TopPost, socialMediaService } from '../../services/socialMediaService';
import { getCollabOwner, isViewsUnavailable, CollabBadge, UnavailableViews } from './postAttribution';

interface PostCardProps {
  post: TopPost;
  customerId: string;
  onClick?: () => void;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

// Scout content_category keys are snake_case (e.g. "player_spotlight") → "Player Spotlight"
const humanizeCategory = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getPlatformIcon = (platformName: string) => {
  switch (platformName.toLowerCase()) {
    case 'instagram': return Instagram;
    case 'tiktok': return Music;
    case 'facebook': return Facebook;
    case 'twitter': case 'x': return Twitter;
    case 'youtube': return Youtube;
    case 'linkedin': return Linkedin;
    default: return Instagram;
  }
};

const getPlatformColor = (platformName: string): string => {
  switch (platformName.toLowerCase()) {
    case 'instagram': return 'text-pink-500';
    case 'tiktok': return 'text-black';
    case 'facebook': return 'text-blue-600';
    case 'twitter': case 'x': return 'text-blue-400';
    case 'youtube': return 'text-red-600';
    case 'linkedin': return 'text-blue-700';
    default: return 'text-gray-500';
  }
};

// Platform-colored top band (Sprout-style). Instagram uses its brand gradient.
const getPlatformBandColor = (platformName: string): string => {
  switch (platformName.toLowerCase()) {
    case 'instagram': return 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400';
    case 'tiktok': return 'bg-black';
    case 'facebook': return 'bg-blue-600';
    case 'twitter': case 'x': return 'bg-sky-400';
    case 'youtube': return 'bg-red-600';
    case 'linkedin': return 'bg-blue-700';
    default: return 'bg-gray-300';
  }
};

const PostCard: React.FC<PostCardProps> = ({ post, customerId, onClick }) => {
  const [imgError, setImgError] = useState(false);

  const PlatformIcon = getPlatformIcon(post.platform.name);
  const platformColor = getPlatformColor(post.platform.name);
  const platformBand = getPlatformBandColor(post.platform.name);

  const collabOwner = getCollabOwner(post.collaboratorUsername);
  const headerUsername = collabOwner || post.account.username;
  const viewsUnavailable = isViewsUnavailable({
    isInstagram: post.platform.name.toLowerCase() === 'instagram',
    views: post.metrics.views,
    postType: post.postType,
    collabOwner,
  });

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
    >
      {/* Platform-colored top band */}
      <div className={`h-1.5 ${platformBand}`} />

      {/* Header: platform logo + account name, post date beneath */}
      <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
        <PlatformIcon className={`h-4 w-4 flex-shrink-0 ${platformColor}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0 h-4">
            <span className="text-xs font-semibold text-gray-900 truncate">@{headerUsername}</span>
            {collabOwner && <CollabBadge owner={collabOwner} creditedTo={post.account.username} />}
          </div>
          <div className="text-[10px] text-gray-400 truncate">
            {collabOwner && <>with @{post.account.username} · </>}
            {formatDate(post.postedAt)}
          </div>
        </div>
      </div>

      {/* Thumbnail */}
      <div className="aspect-video bg-gray-100 relative">
        {post.thumbnailBlobPath && !imgError ? (
          <img
            src={socialMediaService.getThumbnailUrl(post.id, customerId)}
            alt="Post thumbnail"
            className="w-full h-full object-cover"
            crossOrigin="use-credentials"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-1">
            <ImageOff className="h-6 w-6" />
            <span className="text-[10px]">No preview</span>
          </div>
        )}

        {/* Badges overlay */}
        <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
          {post.postType && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-black/60 text-white">
              {post.postType}
            </span>
          )}
          {post.isSponsored && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/90 text-white">
              Sponsored
            </span>
          )}
          {post.isAd && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/90 text-white">
              Ad
            </span>
          )}
        </div>

        {/* Brand Scout content category — shown only for scout-tracked accounts */}
        {post.contentCategory && (
          <span className="absolute bottom-2 left-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/90 text-emerald-700">
            {humanizeCategory(post.contentCategory)}
          </span>
        )}

      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        {/* Caption */}
        {post.caption && (
          <p className="text-[10px] text-gray-500 line-clamp-2">{post.caption}</p>
        )}

        {/* Engagement metrics */}
        {post.postType === 'story' ? (
          // Stories report Graph-API metrics, not likes/comments. Show link clicks,
          // total navigations, and views (order: link_clicks, navigation, views).
          <div className="flex items-center gap-2.5 text-xs text-gray-500">
            <div className="flex items-center gap-0.5" title="Link clicks">
              <MousePointerClick className="h-3 w-3" />
              <span>{formatNumber(post.metrics.extendedMetrics?.link_clicks ?? 0)}</span>
            </div>
            <div className="flex items-center gap-0.5" title="Navigations (tap forward/back, exit, swipe)">
              <Navigation className="h-3 w-3" />
              <span>{formatNumber(post.metrics.extendedMetrics?.navigation?.total ?? 0)}</span>
            </div>
            <div className="flex items-center gap-0.5" title="Views">
              <Eye className="h-3 w-3" />
              <span>{formatNumber(post.metrics.views)}</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2.5 text-xs text-gray-500">
            <div className="flex items-center gap-0.5">
              <Heart className="h-3 w-3" />
              <span>{formatNumber(post.metrics.likes)}</span>
            </div>
            <div className="flex items-center gap-0.5">
              <MessageCircle className="h-3 w-3" />
              <span>{formatNumber(post.metrics.comments)}</span>
            </div>
            <div className="flex items-center gap-0.5">
              <Share2 className="h-3 w-3" />
              <span>{formatNumber(post.metrics.shares)}</span>
            </div>
            {viewsUnavailable ? (
              <UnavailableViews collabOwner={collabOwner} />
            ) : (
              <div className="flex items-center gap-0.5">
                <Eye className="h-3 w-3" />
                <span>{formatNumber(post.metrics.views)}</span>
              </div>
            )}
          </div>
        )}

        {/* EMV + external link */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-0.5 text-xs font-semibold text-emerald-600">
            <DollarSign className="h-3 w-3" />
            <span>{post.metrics.emv}</span>
          </div>
          <div className="flex items-center gap-2">
            {post.postUrl && (
              <a
                href={post.postUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-gray-400 hover:text-blue-500 transition-colors"
                title="View post"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCard;
